import {
  Box,
  Button,
  Code,
  Container,
  Heading,
  HStack,
  Image,
  Text,
  VStack,
  Collapse,
  useDisclosure,
  Icon,
} from '@chakra-ui/react';
import { useRouter, useRouterState } from '@tanstack/react-router';
import { AlertTriangle, ChevronDown, ChevronUp, Home, RefreshCw, ArrowLeft } from 'lucide-react';
import errorImage from '@/assets/images/error.png';
import { GradientBackground } from '@/components/ui';

interface RouteErrorBoundaryProps {
  error: Error;
  reset?: () => void;
}

export function RouteErrorBoundary({ error, reset }: RouteErrorBoundaryProps) {
  const router = useRouter();
  const { location } = useRouterState();
  const { isOpen, onToggle } = useDisclosure();
  const isDev = import.meta.env.DEV;

  const handleGoHome = () => {
    router.navigate({ to: '/' });
  };

  const handleGoBack = () => {
    router.history.back();
  };

  const handleRetry = () => {
    if (reset) {
      reset();
    } else {
      router.invalidate();
    }
  };

  // Determine error type for better messaging
  const getErrorInfo = () => {
    const message = error.message?.toLowerCase() || '';

    if (message.includes('chunk') || message.includes('loading')) {
      return {
        title: 'Loading Error',
        description: 'Failed to load page resources. This might be due to a network issue or a recent update.',
        suggestion: 'Try refreshing the page or clearing your browser cache.',
      };
    }

    if (message.includes('network') || message.includes('fetch')) {
      return {
        title: 'Network Error',
        description: 'Unable to connect to the server.',
        suggestion: 'Please check your internet connection and try again.',
      };
    }

    if (message.includes('not found') || message.includes('404')) {
      return {
        title: 'Page Not Found',
        description: 'The page you\'re looking for doesn\'t exist or has been moved.',
        suggestion: 'Try navigating back or going to the home page.',
      };
    }

    if (message.includes('objects are not valid as a react child')) {
      return {
        title: 'Rendering Error',
        description: 'There was an issue displaying this page.',
        suggestion: 'This is likely a bug. Please try refreshing or report the issue.',
      };
    }

    return {
      title: 'Something Went Wrong',
      description: 'An unexpected error occurred while loading this page.',
      suggestion: 'Please try again or email support@paire.io if the problem persists.',
    };
  };

  const errorInfo = getErrorInfo();

  return (
    <>
      <GradientBackground />
      <Box minH="100vh" position="relative" zIndex={1}>
        <Container maxW="container.md" py={8}>
        <VStack spacing={6} align="center" textAlign="center">
        <Image
          boxSize={'xs'}
          objectFit="cover"
          src={errorImage}
          alt="Error illustration"
        />

        <VStack spacing={2}>
          <HStack>
            <Icon as={AlertTriangle} color="red.400" boxSize={6} />
            <Heading as="h1" size="lg" color="red.400">
              {errorInfo.title}
            </Heading>
          </HStack>

          <Text fontSize="lg" color="gray.300">
            {errorInfo.description}
          </Text>

          <Text fontSize="sm" color="gray.500">
            {errorInfo.suggestion}
          </Text>
        </VStack>

        <HStack spacing={3} flexWrap="wrap" justify="center">
          <Button
            leftIcon={<RefreshCw size={18} />}
            bg="purple.500"
            color="white"
            _hover={{ bg: 'purple.600' }}
            onClick={handleRetry}
          >
            Try Again
          </Button>

          <Button
            leftIcon={<ArrowLeft size={18} />}
            bg={'white'}
            onClick={handleGoBack}
          >
            Go Back
          </Button>

          <Button
            leftIcon={<Home size={18} />}
            bg={'white'}
            onClick={handleGoHome}
          >
            Home
          </Button>
        </HStack>

        {isDev && (
          <Box w="full" mt={4}>
            <Button
              size="sm"
              variant="ghost"
              onClick={onToggle}
              rightIcon={isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              color="gray.500"
            >
              {isOpen ? 'Hide' : 'Show'} Error Details
            </Button>

            <Collapse in={isOpen}>
              <Box
                mt={3}
                p={4}
                bg="gray.800"
                borderRadius="md"
                textAlign="left"
                overflow="auto"
                maxH="300px"
              >
                <Text fontSize="xs" color="gray.500" mb={2}>
                  Route: {location.pathname}
                </Text>

                <Text fontSize="sm" fontWeight="bold" color="red.300" mb={2}>
                  {error.name}: {error.message}
                </Text>

                {error.stack && (
                  <Code
                    display="block"
                    whiteSpace="pre-wrap"
                    fontSize="xs"
                    p={3}
                    bg="gray.900"
                    borderRadius="sm"
                    color="gray.400"
                  >
                    {error.stack}
                  </Code>
                )}
              </Box>
            </Collapse>
          </Box>
        )}

        <Text fontSize="xs" color="gray.600" mt={4}>
          Error ID: {Date.now().toString(36)}
        </Text>
      </VStack>
      </Container>
      </Box>
    </>
  );
}
